import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Link } from 'react-router-dom';
import { Package, AlertTriangle, TrendingDown, Activity, ShoppingCart, Users, DollarSign, TrendingUp } from 'lucide-react';
import { stockService } from '../api/stock';
import { dispenseService } from '../api/dispense';
import { purchaseService } from '../api/purchases';
import { supabase } from '../../../lib/supabase';
import type { StockSummary, Dispense, Purchase } from '../types';

interface SalesStats {
  totalSales: number;
  todaySales: number;
  todayDiscount: number;
  salesCount: number;
}

export default function PharmacyOverview() {
  const { formatCurrency } = useCurrency();
  const [stockSummary, setStockSummary] = useState<StockSummary[]>([]);
  const [todaysDispenses, setTodaysDispenses] = useState<Dispense[]>([]);
  const [, setRecentPurchases] = useState<Purchase[]>([]);
  const [salesStats, setSalesStats] = useState<SalesStats>({
    totalSales: 0,
    todaySales: 0,
    todayDiscount: 0,
    salesCount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [summary, dispenses, purchases] = await Promise.all([
          stockService.getSummary(),
          dispenseService.getTodaysDispenses(),
          purchaseService.getRecent(5)
        ]);
        
        setStockSummary(summary);
        setTodaysDispenses(dispenses);
        setRecentPurchases(purchases);

        await loadSalesStats();
      } catch (error) {
        console.error('Failed to load pharmacy overview data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const loadSalesStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data: todaySalesData, error: todayError } = await supabase
        .from('sales_invoices')
        .select('total, discount_amount')
        .gte('created_at', `${today}T00:00:00`)
        .lt('created_at', `${today}T23:59:59`);

      if (todayError) throw todayError;

      const { data: totalSalesData, error: totalError } = await supabase
        .from('sales_invoices')
        .select('total');

      if (totalError) throw totalError;

      const todaySales = todaySalesData?.reduce((sum, inv) => sum + (inv.total || 0), 0) || 0;
      const todayDiscount = todaySalesData?.reduce((sum, inv) => sum + (inv.discount_amount || 0), 0) || 0;
      const totalSales = totalSalesData?.reduce((sum, inv) => sum + (inv.total || 0), 0) || 0;

      setSalesStats({
        totalSales,
        todaySales,
        todayDiscount,
        salesCount: todaySalesData?.length || 0
      });
    } catch (error) {
      console.error('Failed to load sales stats:', error);
    }
  };

  const totalMedicines = stockSummary.length;
  const nearExpiryCount = stockSummary.filter(s => s.isNearExpiry).length;
  const lowStockCount = stockSummary.filter(s => s.isLowStock).length;
  const todaysDispenseCount = todaysDispenses.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-primary-900">Pharmacy Dashboard</h1>
          <p className="text-sm text-neutral-600">Manage medicines, stock, sales, and dispensing</p>
        </div>
      </header>

      {/* Sales Stats Cards - Matching Reference Design */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary-100 uppercase tracking-wide">Total Sales</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(salesStats.totalSales)}</p>
              <p className="text-xs text-primary-100 mt-1">All time revenue</p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
              <DollarSign className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-100 uppercase tracking-wide">Today Sales</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(salesStats.todaySales)}</p>
              <p className="text-xs text-green-100 mt-1">{salesStats.salesCount} transactions</p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
              <TrendingUp className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-100 uppercase tracking-wide">Today Discount</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(salesStats.todayDiscount)}</p>
              <p className="text-xs text-amber-100 mt-1">Discounts given today</p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
              <DollarSign className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-neutral-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Medicines</p>
              <p className="text-2xl font-bold text-neutral-900">{totalMedicines}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-amber-100 p-3 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Near Expiry</p>
              <p className="text-2xl font-bold text-neutral-900">{nearExpiryCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-red-100 p-3 rounded-lg">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Low Stock</p>
              <p className="text-2xl font-bold text-neutral-900">{lowStockCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Today's Dispenses</p>
              <p className="text-2xl font-bold text-neutral-900">{todaysDispenseCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link
          to="/pharmacy/medicines"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Package className="h-6 w-6 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Medicines</h3>
              <p className="text-sm text-neutral-600">Manage medicine master data</p>
            </div>
          </div>
        </Link>

        <Link
          to="/pharmacy/stock"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <TrendingDown className="h-6 w-6 text-amber-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Stock Management</h3>
              <p className="text-sm text-neutral-600">View batch stock and expiry</p>
            </div>
          </div>
        </Link>

        <Link
          to="/pharmacy/purchases"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <ShoppingCart className="h-6 w-6 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Purchases</h3>
              <p className="text-sm text-neutral-600">Record medicine purchases</p>
            </div>
          </div>
        </Link>

        <Link
          to="/pharmacy/dispense"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Activity className="h-6 w-6 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Dispense</h3>
              <p className="text-sm text-neutral-600">Dispense medicines to patients</p>
            </div>
          </div>
        </Link>

        <Link
          to="/pharmacy/suppliers"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Users className="h-6 w-6 text-indigo-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Suppliers</h3>
              <p className="text-sm text-neutral-600">Manage supplier information</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Near Expiry Items */}
        <div className="bg-white rounded-lg border">
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Near Expiry (≤30 days)</h3>
              <Link
                to="/pharmacy/stock?filter=near-expiry"
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {nearExpiryCount === 0 ? (
              <p className="text-sm text-neutral-500">No medicines near expiry</p>
            ) : (
              <div className="space-y-3">
                {stockSummary
                  .filter(s => s.isNearExpiry)
                  .slice(0, 5)
                  .map((item) => (
                    <div key={item.medicineId} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.medicineName}</p>
                        <p className="text-sm text-neutral-600">
                          Expires: {new Date(item.nearestExpiry).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-sm font-medium text-amber-600">
                        {item.totalStock} units
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Low Stock Items */}
        <div className="bg-white rounded-lg border">
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Low Stock</h3>
              <Link
                to="/pharmacy/stock?filter=low-stock"
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {lowStockCount === 0 ? (
              <p className="text-sm text-neutral-500">No medicines with low stock</p>
            ) : (
              <div className="space-y-3">
                {stockSummary
                  .filter(s => s.isLowStock)
                  .slice(0, 5)
                  .map((item) => (
                    <div key={item.medicineId} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.medicineName}</p>
                        <p className="text-sm text-neutral-600">
                          Reorder level: {item.reorderLevel}
                        </p>
                      </div>
                      <span className="text-sm font-medium text-red-600">
                        {item.totalStock} units
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
